import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Avatar,
  Button,
  Callout,
  Composer,
  ConfirmDialog,
  EmptyState,
  IconButton,
  IconChevronDown,
  IconMessage,
  IconPlus,
  IconTrash,
  Menu,
  MenuItem,
  MenuLabel,
  Segmented,
  Workspace,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import InsightsMarkdown from './InsightsMarkdown';
import { groupSavedChats, type SavedChatSummary } from './insightsChatGroups';

type ReasoningEffort = 'low' | 'medium' | 'high';

type ChatStep = {
  tool: string;
  purpose?: string;
  summary?: string;
  sql?: string;
  row_count?: number | null;
  truncated?: boolean;
  error?: string;
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  steps?: ChatStep[];
};

const STORAGE_KEY = 'platformAdminInsightsChatId';

type SavedChat = SavedChatSummary & {
  reasoning_effort: ReasoningEffort;
};

const SavedChatsMenu: React.FC<{
  chats: SavedChat[];
  chatId: number | null;
  disabled: boolean;
  disabledReason?: string;
  onSelect: (id: number) => void;
  onDelete: (chat: SavedChatSummary) => void;
}> = ({ chats, chatId, disabled, disabledReason, onSelect, onDelete }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [anchorBox, setAnchorBox] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const groups = groupSavedChats(chats);
  const current = chats.find((chat) => chat.id === chatId);
  const label =
    current?.title || (chats.length ? 'Saved chats' : 'No saved chats');

  const placeMenu = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = {
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
    };
    setAnchorBox((current) =>
      current &&
      current.top === next.top &&
      current.left === next.left &&
      current.width === next.width
        ? current
        : next,
    );
  }, []);

  // Portaled with position:fixed so the card's overflow-clip does not cut
  // the list off. Re-measure while it is open so scroll and resize keep it
  // on the trigger.
  useEffect(() => {
    if (!open) return;
    placeMenu();
    const onMove = () => placeMenu();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, placeMenu]);

  return (
    <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
      <Button
        ref={anchorRef}
        variant="secondary"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Saved chats"
        disabled={disabled}
        disabledReason={disabledReason}
        onClick={() => {
          setOpen((value) => {
            const next = !value;
            if (next) placeMenu();
            return next;
          });
        }}
        trailing={
          <IconChevronDown
            size={14}
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          />
        }
        className="w-full !justify-between"
      >
        <span className="min-w-0 flex-1 truncate text-left font-medium">
          {label}
        </span>
      </Button>
      {open &&
        groups.length > 0 &&
        anchorBox &&
        createPortal(
          <div
            className="av2 fixed z-[80]"
            style={{
              top: anchorBox.top,
              left: anchorBox.left,
              width: anchorBox.width,
            }}
          >
            <SavedChatsList
              groups={groups}
              chatId={chatId}
              anchorRef={anchorRef}
              width={anchorBox.width}
              onClose={() => setOpen(false)}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

const SavedChatsList: React.FC<{
  groups: ReturnType<typeof groupSavedChats>;
  chatId: number | null;
  anchorRef: React.RefObject<HTMLButtonElement>;
  width: number;
  onClose: () => void;
  onSelect: (id: number) => void;
  onDelete: (chat: SavedChatSummary) => void;
}> = ({ groups, chatId, anchorRef, width, onClose, onSelect, onDelete }) => (
  <Menu
    open
    onClose={onClose}
    anchorRef={anchorRef}
    width={width}
    className="!static !mt-0 max-h-80 overflow-y-auto"
  >
    {groups.map((group) => (
      <div key={group.label}>
        <MenuLabel>{group.label}</MenuLabel>
        {group.chats.map((chat) => {
          const title = chat.title || 'New chat';
          return (
            <div key={chat.id} className="flex items-center">
              <MenuItem
                selected={chat.id === chatId}
                className="min-w-0 flex-1"
                onClick={() => {
                  onClose();
                  onSelect(chat.id);
                }}
              >
                {title}
              </MenuItem>
              <IconButton
                size="sm"
                label={`Delete ${title}`}
                className="mr-1 hover:text-v2-danger"
                onClick={() => {
                  onClose();
                  onDelete(chat);
                }}
              >
                <IconTrash size={14} />
              </IconButton>
            </div>
          );
        })}
      </div>
    ))}
  </Menu>
);

const REASONING_OPTIONS: {
  value: ReasoningEffort;
  label: string;
  hint: string;
}[] = [
  { value: 'low', label: 'Low', hint: 'Faster, lighter answers' },
  { value: 'medium', label: 'Medium', hint: 'Balanced' },
  { value: 'high', label: 'High', hint: 'Slower, more careful' },
];

const SUGGESTIONS = [
  'Anything unusual about the last 10 calls to a store?',
  'How does reservation booking work in the code?',
  'What are order trends over the last 30 days?',
];

const loadStoredChatId = (): number | null => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const asMessages = (rows: unknown): ChatMessage[] => {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const message = row as ChatMessage;
      return {
        id: String(message.id),
        role: message.role === 'assistant' ? 'assistant' : 'user',
        content: message.content || '',
        steps: Array.isArray(message.steps) ? message.steps : [],
      };
    });
};

const InsightsChatTab: React.FC = () => {
  const [chatId, setChatId] = useState<number | null>(loadStoredChatId);
  const activeChatId = useRef(chatId);
  const rememberChat = (id: number | null) => {
    activeChatId.current = id;
    setChatId(id);
  };
  const [chats, setChats] = useState<SavedChat[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reasoning, setReasoning] = useState<ReasoningEffort>('medium');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [contextTrimmed, setContextTrimmed] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<SavedChatSummary | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const refreshChats = async () => {
    const response = await api.get(apiEndpoints.platformInsightsChats(), {
      skipStoreHeader: true,
    });
    if (!response?.ok) return;
    const data = await response.json().catch(() => ({}));
    setChats(Array.isArray(data.chats) ? data.chats : []);
  };

  const openChat = async (id: number) => {
    rememberChat(id);
    setError('');
    setContextTrimmed(false);
    const response = await api.get(
      apiEndpoints.platformInsightsChatDetail(id),
      {
        skipStoreHeader: true,
      },
    );
    if (activeChatId.current !== id) return;
    if (!response) return;
    const data = await response.json().catch(() => ({}));
    if (activeChatId.current !== id) return;
    if (!response.ok) {
      rememberChat(null);
      setMessages([]);
      return;
    }
    setMessages(asMessages(data.messages));
    if (
      REASONING_OPTIONS.some((option) => option.value === data.reasoning_effort)
    ) {
      setReasoning(data.reasoning_effort);
    }
  };

  const deleteChat = async (id: number) => {
    setDeleteError('');
    try {
      const response = await api.delete(
        apiEndpoints.platformInsightsChatDetail(id),
        { skipStoreHeader: true },
      );
      if (!response) {
        setDeleteError('You need to be signed in as a platform admin.');
        return false;
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setDeleteError(data.error || 'The chat could not be deleted.');
        return false;
      }
      setChats((current) => current.filter((chat) => chat.id !== id));
      if (activeChatId.current !== id) return true;
      rememberChat(null);
      setMessages([]);
      setError('');
      setContextTrimmed(false);
      setDraft('');
      return true;
    } catch {
      setDeleteError('The chat could not be deleted.');
      return false;
    }
  };

  useEffect(() => {
    void refreshChats();
    const storedId = loadStoredChatId();
    if (storedId) void openChat(storedId);
    // The stored chat is loaded once when the tab opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatId) sessionStorage.setItem(STORAGE_KEY, String(chatId));
    else sessionStorage.removeItem(STORAGE_KEY);
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || loading) return;

    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: question,
    };
    setMessages((current) => [...current, userMessage]);
    setDraft('');
    setError('');
    setLoading(true);

    try {
      const response = await api.post(
        apiEndpoints.platformInsightsChat(),
        {
          chat_id: chatId,
          content: question,
          reasoning_effort: reasoning,
        },
        { skipStoreHeader: true },
      );
      if (!response) {
        setError('You need to be signed in as a platform admin.');
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(
          data.error || data.detail || 'The assistant could not answer.',
        );
        return;
      }
      if (data.chat?.id) rememberChat(data.chat.id);
      setContextTrimmed(Boolean(data.context_trimmed));
      setMessages((current) => [
        ...current,
        {
          id: String(data.message?.id || `${Date.now()}-assistant`),
          role: 'assistant',
          content: data.answer || 'No answer was returned.',
          steps: Array.isArray(data.steps) ? data.steps : [],
        },
      ]);
      void refreshChats();
    } catch {
      setError('The assistant could not be reached.');
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = () => {
    rememberChat(null);
    setMessages([]);
    setError('');
    setContextTrimmed(false);
    setDraft('');
  };

  const reasoningHint = REASONING_OPTIONS.find(
    (option) => option.value === reasoning,
  )?.hint;

  return (
    <div className="av2 flex h-[calc(100dvh-15rem)] min-h-[32rem] flex-col">
      <Workspace
        className="min-h-0 flex-1"
        title="Insights"
        description="This chat keeps context. Chats are saved for six months. It can search the web, read the code, or look at the database, and it cannot change data."
        actions={
          <Button
            variant="secondary"
            leading={<IconPlus size={14} />}
            disabled={loading}
            disabledReason={
              loading ? 'Wait until the answer finishes.' : undefined
            }
            onClick={startNewChat}
          >
            New chat
          </Button>
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 lg:px-6">
            <SavedChatsMenu
              chats={chats}
              chatId={chatId}
              disabled={loading || chats.length === 0}
              disabledReason={
                loading
                  ? 'Wait until the answer finishes.'
                  : chats.length === 0
                    ? 'No saved chats yet.'
                    : undefined
              }
              onSelect={(id) => void openChat(id)}
              onDelete={(chat) => {
                setDeleteError('');
                setChatToDelete(chat);
              }}
            />
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-v2-12 text-v2-fg-3">Reasoning</span>
              <Segmented
                aria-label="Reasoning level"
                items={REASONING_OPTIONS.map((option) => ({
                  key: option.value,
                  label: option.label,
                }))}
                value={reasoning}
                onChange={(value) => {
                  if (!loading) setReasoning(value);
                }}
                className={
                  loading ? 'pointer-events-none opacity-50' : undefined
                }
              />
              <span className="text-v2-12 text-v2-fg-4">{reasoningHint}</span>
            </div>
          </div>
        }
      >
        {contextTrimmed && (
          <Callout
            tone="warning"
            className="mx-4 mt-3 lg:mx-6"
            title="Older messages were left out"
          >
            This thread is past the limit. The latest reply did not include the
            oldest messages. The full chat is still saved. Start a new chat if
            the assistant needs to see everything.
          </Callout>
        )}

        <div className="v2-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 lg:px-6">
          {messages.length === 0 ? (
            <div className="mx-auto flex max-w-xl flex-col items-center">
              <EmptyState
                compact
                icon={<IconMessage />}
                title="What do you want to know?"
                description="It can answer directly, search the web, read the code, or query the database. You pick how hard it thinks."
              />
              <div className="flex w-full max-w-md flex-col gap-2">
                {SUGGESTIONS.map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="secondary"
                    disabled={loading}
                    disabledReason={
                      loading ? 'Wait until the answer finishes.' : undefined
                    }
                    className="!h-auto w-full !justify-start !whitespace-normal py-2 text-left"
                    onClick={() => void send(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <ol className="flex flex-col gap-4" aria-label="Messages">
              {messages.map((message) => {
                const assistant = message.role === 'assistant';
                return (
                  <li
                    key={message.id}
                    className="grid grid-cols-[28px_minmax(0,1fr)] gap-x-3"
                  >
                    <span className="pt-0.5">
                      {assistant ? (
                        <Avatar arthur size="sm" />
                      ) : (
                        <Avatar name="You" size="sm" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div
                        className={`text-v2-12 font-semibold ${
                          assistant ? 'text-v2-accent' : 'text-v2-fg'
                        }`}
                      >
                        {assistant ? 'Insights' : 'You'}
                      </div>
                      <div className="mt-0.5 max-w-[68ch] text-v2-13 leading-5 text-v2-fg [overflow-wrap:anywhere]">
                        {assistant ? (
                          <InsightsMarkdown text={message.content} />
                        ) : (
                          <p className="whitespace-pre-wrap">
                            {message.content}
                          </p>
                        )}
                      </div>
                      {message.steps && message.steps.length > 0 && (
                        <details className="mt-2 max-w-[68ch] text-v2-12 text-v2-fg-3">
                          <summary className="cursor-pointer font-medium text-v2-fg-2">
                            How this was answered ({message.steps.length})
                          </summary>
                          <ul className="mt-2 space-y-2">
                            {message.steps.map((step, index) => (
                              <li key={`${message.id}-step-${index}`}>
                                <p>
                                  {step.purpose || step.summary || step.tool}
                                </p>
                                {step.sql && (
                                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded-v2 bg-v2-sunken p-2 font-mono text-v2-11 text-v2-fg">
                                    {step.sql}
                                  </pre>
                                )}
                                {step.error && (
                                  <p className="mt-1 text-v2-danger">
                                    {step.error}
                                  </p>
                                )}
                                {typeof step.row_count === 'number' && (
                                  <p className="v2-num mt-1">
                                    {`${step.row_count} ${
                                      step.row_count === 1 ? 'row' : 'rows'
                                    }${step.truncated ? ', truncated' : ''}`}
                                  </p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {loading && (
            <div
              role="status"
              className="mt-4 grid grid-cols-[28px_minmax(0,1fr)] gap-x-3"
            >
              <span className="pt-0.5">
                <Avatar arthur size="sm" />
              </span>
              <div>
                <div className="text-v2-12 font-semibold text-v2-accent">
                  Insights
                </div>
                <p className="mt-0.5 text-v2-13 text-v2-fg-3">
                  Reading the database…
                </p>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <Composer
          value={draft}
          onChange={setDraft}
          onSend={() => void send(draft)}
          sending={loading}
          error={error || null}
          onDismissError={() => setError('')}
          placeholder="Ask anything. It can search the web, read the code, or query the database."
          aside={
            contextTrimmed
              ? undefined
              : 'A reply keeps about 80,000 tokens of this chat. The full chat stays saved for six months.'
          }
        />
      </Workspace>

      <ConfirmDialog
        open={chatToDelete != null}
        onClose={() => {
          if (deleting) return;
          setDeleteError('');
          setChatToDelete(null);
        }}
        busy={deleting}
        title="Delete this chat?"
        consequence={`“${chatToDelete?.title || 'New chat'}” will be removed. This cannot be undone.`}
        confirmLabel="Delete chat"
        onConfirm={async () => {
          if (!chatToDelete) return;
          setDeleting(true);
          try {
            const removed = await deleteChat(chatToDelete.id);
            if (removed) setChatToDelete(null);
          } finally {
            setDeleting(false);
          }
        }}
      >
        {deleteError ? (
          <span className="text-v2-danger">{deleteError}</span>
        ) : null}
      </ConfirmDialog>
    </div>
  );
};

export default InsightsChatTab;
